import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AppButton, Box, Preloader, RawButton, RoundPendingIcon, Text } from '@common-fe/common-fe';

import Modal from '@/components/elements/AutoBlockedModal/Modal';

import { useFileDuplicationProcessQuery } from './queries';

interface Props {
  fileId: string;
  onSearchDuplicate: () => void;
  visible: boolean;
  onSetVisible: (value: boolean) => void;
  onCleanProcessedFile: (id: string) => void;
}
const FileDuplicationProcess: React.FC<Props> = ({
  onCleanProcessedFile,
  fileId,
  onSearchDuplicate,
  onSetVisible,
  visible,
}) => {
  const { handleProcess, isLoading } = useFileDuplicationProcessQuery(fileId);

  const handleOpenDuplicatesSearch = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      onSearchDuplicate();
    },
    [onSearchDuplicate]
  );
  const handleSubmit = useCallback(async () => {
    await handleProcess();
    onCleanProcessedFile(fileId);
    onSetVisible(false);
  }, [fileId, handleProcess, onCleanProcessedFile, onSetVisible]);

  return (
    <>
      {createPortal(
        <Modal testId="file-duplication-process" visible={visible} onSetVisible={onSetVisible}>
          <Box direction="column" align="center" justify="center">
            <RoundPendingIcon size="48px" color="warning" />
            <Text size="2xl" color="textTitle" weight={700} margin={{ top: 'spacing12' }}>
              Process potentially duplicate file?
            </Text>
            <Box direction="row" align="center" margin={{ top: 'spacing12' }}>
              <Text size="medium" color="textBody" weight={400}>
                This file is a duplicate of existing files.
              </Text>
              <RawButton
                data-testId="file-duplication-process_show-d"
                margin={{ left: 'spacing2' }}
                onClick={handleOpenDuplicatesSearch}
              >
                <Text size="medium" color="textAccent" weight={400}>
                  Search for Duplicates
                </Text>
              </RawButton>
            </Box>
            <Box direction="row" align="center" justify="center" margin={{ vertical: 'spacing24' }}>
              <Box width={{ max: '180px', width: '180px' }}>
                <AppButton
                  fill
                  testId="file-duplication-process_cancel"
                  buttonType="secondary"
                  onClick={() => onSetVisible(false)}
                >
                  Cancel
                </AppButton>
              </Box>
              <Box width="spacing12"></Box>
              <Box width={{ max: '180px', width: '180px' }}>
                <AppButton
                  testId="file-duplication-process_process"
                  fill
                  disabled={isLoading}
                  onClick={handleSubmit}
                >
                  {isLoading ? <Preloader testId="file-duplication-process_loader" /> : <>Process File</>}
                </AppButton>
              </Box>
            </Box>
          </Box>
        </Modal>,
        document.body
      )}
    </>
  );
};

export default FileDuplicationProcess;
